import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';
import loadable from '@loadable/component';
import { ui, define, when } from '@owenscorning/pcb.alpha';
import _ from 'lodash';
import { Component } from 'react';

import Theme from './Theme';
import DeltaRenderer from '../../../../../helpers/delta_renderer';
import draftToDelta, { isDraftRaw } from '../../../../../helpers/draft_to_delta';

const Edit = loadable(() => import('./Editor'), {
  fallback: <div>Loading...</div>,
});

const Wrapper = styled.div`
  ${props =>
    props.inline
      ? css`
          p {
            margin: 0;
          }
        `
      : props.singular
      ? css``
      : css`
          margin: 0.96em 0;
        `}
  .ql-container {
    background-color: transparent !important;
    border: none !important;
    color: inherit !important;
    font-family: inherit !important;
    font-size: inherit !important;
  }

  .ql-editor {
    font-size: inherit !important;
    line-height: inherit !important;
    max-height: none;
    overflow-y: visible !important;
    padding: 0 !important;
  }

  .ql-editor > * {
    cursor: default;
  }
`;

export const isDeltaEmpty = delta =>
  typeof delta === 'undefined'
    ? true
    : delta?.ops?.reduce(
        (empty, op) =>
          (_.isString(op?.insert) && op.insert.trim().replace('↵', '').replace('\n', '').replace('\\n', '') != '') ||
          (!_.isString(op?.insert) && !op?.insert?.breaker)
            ? false
            : empty,
        true
      );

class View extends Component {
  constructor(props) {
    super(props);
    this.delta = this.delta.bind(this);
    this.draft = this.draft.bind(this);
    this.html = this.html.bind(this);
  }

  get content() {
    return this.props.content;
  }

  get inline() {
    return this.props.inline;
  }

  get singular() {
    return this.props.singular;
  }

  delta(content) {
    if (!_.isPlainObject(content)) return null;
    if (isDeltaEmpty(content)) return <></>;
    return (
      <Wrapper singular={this.singular} inline={this.inline} css={[Theme.base, Theme.list]}>
        <UI.Text.Parsing content={new DeltaRenderer(content).render()} />
      </Wrapper>
    );
  }

  draft(content) {
    try {
      const parsedContent = JSON.parse(content);
      if (isDraftRaw(parsedContent)) return this.delta(draftToDelta(parsedContent));
    } catch (e) {}
    return null;
  }

  html(content) {
    if (!_.isString(content)) return false;
    return (
      <Wrapper singular={this.singular} inline={this.inline} css={[Theme.base, Theme.list]}>
        <UI.Text.Parsing content={(content || '').match(/<body[^>]*?>([\s\S]*)<\/body>/)?.[1] || content || ''} />
      </Wrapper>
    );
  }

  render() {
    return _.reduce(
      [this.delta, this.draft, this.html],
      (result, format) => {
        if (result) return result;
        return format(this.content);
      },
      null
    );
  }
}

export default define`Rich`('0.0.1')({
  edit: props => <Edit {...props} />,
  view: props => <View {...props} />,
});
